import React from 'react'
import { Link } from '@tanstack/react-router'

export interface BreadcrumbItem {
  label: string
  href?: string // undefined for current page
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[]
}

export default function Breadcrumbs({ items }: BreadcrumbsProps) {
  // Don't render if no items
  if (!items || items.length === 0) {
    return null
  }

  // For mobile, show compact format if more than 3 items
  const showCompact = items.length > 3

  return (
    <nav
      className="text-sm text-brand-gray font-body mb-4"
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center flex-wrap">
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          const isFirst = index === 0

          // On mobile, show only first, ellipsis, and last if more than 3 items
          const shouldHideOnMobile = showCompact && !isFirst && !isLast && index !== 1

          return (
            <li
              key={`${item.label}-${index}`}
              className={`flex items-center ${shouldHideOnMobile ? 'hidden md:flex' : 'flex'}`}
            >
              {/* Show ellipsis on mobile for middle items */}
              {showCompact && index === 1 && (
                <span className="md:hidden text-brand-gray">...</span>
              )}

              {/* Breadcrumb item */}
              {isLast || !item.href ? (
                // Current page - not clickable
                <span
                  className="text-white font-medium"
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              ) : (
                // Parent page - clickable link
                <Link
                  to={item.href}
                  className="text-brand-gray hover:text-brand-primary transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-dark-light rounded"
                  tabIndex={0}
                >
                  {item.label}
                </Link>
              )}

              {/* Separator - not shown for last item */}
              {!isLast && (
                <span
                  className="mx-2 text-brand-gray select-none"
                  aria-hidden="true"
                >
                  ›
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
